import {setCustLinks} from '../data/DataStore';

/**
 * Regex used to extract the custom regular expression templates from a CustomLinkUrl. Matches contain two capturing groups.
 *
 * Full Match: The full match (ex: "re2:1")
 *
 * Group 1: Represents the regex to use, located in the CustomLinkReplacementRegexes array.
 *
 * Group 2: Represents the group result to use if the custom regex had any matches.
 */
export const CUSTOM_LINK_REGEX = /re(\d+):(\d+)/g;
export const NS_VERSION = '2.6';

/**
 * Builds the "custLink" query string parameter which is part of the GET request to /ContentByQuery.
 *
 * Uses the CustomLink and CustomLinkReplacementRegexes values to scrape important profile information from the current URL.
 * @param {[{}]} customLinks The CustomLinks located on the customers profile config.
 * @param {string[]} customLinkReplacementRegexes The regexes associated with the custom links for the customers profile.
 * @param {string} windowHref The current href (URL) of the current page.
 * @returns The constructed query string parameter or an empty string.
 */
export function buildCustomLinkQueryStringParameters(customLinks, customLinkReplacementRegexes, windowHref) {
	if (!customLinks || !customLinkReplacementRegexes || !windowHref) { return ''; }
	if (customLinks.length === 0 || customLinkReplacementRegexes.length === 0) { return ''; }

	const url = getUrlFromCustomLinks(customLinks);
	const customLinkTokens = getCustomLinkTokensFromUrl(url);
	const tokenMatches = getTokenRegexMatchesFromWindowUrl(customLinkTokens, customLinkReplacementRegexes, windowHref);

	const builtCustomLinks = constructCompleteQueryString(customLinkTokens, tokenMatches);
	setCustLinks(builtCustomLinks)

	return builtCustomLinks;
}

/**
 * Runs a regex against the CustomLink URL to find all custom link tokens.
 * @param {string} url The URL that contains the custom link tokens.
 * @returns {string[]} An array containing all of the custom link token matches which are used as the custLink fields.
 */
function getCustomLinkTokensFromUrl(url) {
	let customLinkTokens = [];

	let match;
	while (match = CUSTOM_LINK_REGEX.exec(url)) {		// eslint-disable-line no-cond-assign
		let matchResults = {
			fullMatch: match[0],
			regexIndex: getRegexIndexFromToken(match[1]),
			groupIndex: match[2]
		};

		customLinkTokens.push(matchResults);
	}

	return customLinkTokens;
}

/**
 * Gets the match for each regex associated with the provided customLinkTokens against the provided window href.
 *
 * @param {string[]} customLinkTokens The custom link replacement tokens.
 * @param {string[]} customLinkReplacementRegexes The regexes associated with the custom links for the customers profile.
 * @param {string} windowHref The current href (URL) of the current page.
 * @returns {string[]} A string array containing the regex matches which are used as the custLink field values.
 */
function getTokenRegexMatchesFromWindowUrl(customLinkTokens, customLinkReplacementRegexes, windowHref) {
	let regexMatches = [];

	if (!customLinkTokens.length) { return regexMatches; }

	customLinkTokens.forEach(token => {
		let regex = new RegExp(customLinkReplacementRegexes[token.regexIndex]);
		let urlMatch = windowHref.match(regex);

		if (urlMatch) {
			regexMatches.push(urlMatch[token.groupIndex])
		}
	});

	return regexMatches;
}

export function parseQueryFromDocument(config, textToParseFrom)
{
	var newQuery = null;

	if (config && textToParseFrom) {
		if (config.hasOwnProperty('ISBNRegex') && config.ISBNRegex !== null) {
			newQuery = findISBNFromRegex(config.ISBNRegex, textToParseFrom);
		}

		if (!newQuery && config.hasOwnProperty('UPCRegex') && config.UPCRegex !== null) {
			newQuery = findUPCFromRegex(config.UPCRegex, textToParseFrom);
		}
	}

	return newQuery;
}

export function findISBNFromRegex(isbnRegexFromConfig, textToParseFrom) {
	var query = null;

	var isbnRegex = new RegExp(isbnRegexFromConfig);
	var m = textToParseFrom.match(isbnRegex);

	if (m && m[0] !== '') {
		query = { ClientIdentifier: m[1].replace(/[^0-9X]/gi, ''), ISBN: m[1] };
	}

	return query;
}

export function findUPCFromRegex(upcRegexFromConfig, textToParseFrom) {
	var query = null;

	var upcRegex = new RegExp(upcRegexFromConfig);
	var m = textToParseFrom.match(upcRegex);

	if (m && m[0] !== "") {
		query = { ClientIdentifier: m[1].replace(/[^0-9]/gi, ''), UPC: m[1] };
	}

	return query;
}

export function isValidQuery(query) {
	var ret = false;

	if (query) {
		var keys = ["Author", "Title", "ISBN", "UPC"];
		for (var k in keys) {
			if (query.hasOwnProperty(keys[k])) {
				var value = query[keys[k]];
				if (value) {
					ret = true;
				}
			}
		}
	}

	return ret;
}

export function buildDefaultQueryStringParameters(query) {
	var queryParams = "&version=" + encodeURI(NS_VERSION);

	var keys = ["Author", "Title", "ISBN", "UPC"];
	for (var k in keys) {
		if (query.hasOwnProperty(keys[k])) {
			var value = query[keys[k]];
			if (value) {
				queryParams += "&" + keys[k] + "=" + encodeURI(value);
			}
		}
	}
	return queryParams;
}

/**
 * Constructs the final query string parameter from the custom link replacement tokens and their matches.
 * @param {any} customLinkTokens The fields of the query string (ex: "re1:1").
 * @param {any} customLinkMatches The values of the query string (ex: "lang").
 * @returns {string} The completed customLink query string parameters.
 */
function constructCompleteQueryString(customLinkTokens, customLinkMatches) {
	let custLinkQueryString = '';

	if (!customLinkMatches.length) { return custLinkQueryString; }

	for (var i = 0; i < customLinkTokens.length; i++) {
		const token = customLinkTokens[i].fullMatch;
		const value = customLinkMatches[i];

		custLinkQueryString += `&custLink=${token}~~${value}`;
	}

	return custLinkQueryString;
}

/**
 * Converts the token's regex 1-based index to 0-based value.
 * @param {number} tokenValue
 */
function getRegexIndexFromToken(tokenValue) {
	return tokenValue - 1;
}

/**
 * Gets the first URL from the provided custom links array.
 * @param {string[]} customLinks
 * @returns {string} The custom link URL.
 */
function getUrlFromCustomLinks(customLinks) {
	return customLinks[0].Url;
}



// WEBPACK FOOTER //
// ./src/lib/ContentByQueryUtilities.js